import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPageSize } from '@tiny-fox/storybook-renderer';
import { Page } from '../types/storybook';
import CanonicalPageRenderer from './CanonicalPageRenderer';

export interface ReviewPagePreviewProps {
  page: Page;
  canvasSize: string;
  ariaLabel: string;
  testId?: string;
  className?: string;
  resolveImageUrl?: (url: string) => string;
}

/**
 * Fits the canonical, print-faithful renderer inside a responsive review card.
 * The printable surface is scaled as one unit so typography and geometry do not
 * get independently reflowed in thumbnails.
 */
const ReviewPagePreview: React.FC<ReviewPagePreviewProps> = ({
  page,
  canvasSize,
  ariaLabel,
  testId,
  className = '',
  resolveImageUrl
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const pageSize = useMemo(() => getPageSize(canvasSize), [canvasSize]);
  const [scale, setScale] = useState(0.25);

  const fitPreview = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const bounds = frame.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    setScale(Math.min(bounds.width / pageSize.width, bounds.height / pageSize.height));
  }, [pageSize.height, pageSize.width]);

  useEffect(() => {
    fitPreview();
    if (typeof ResizeObserver !== 'undefined' && frameRef.current) {
      const observer = new ResizeObserver(fitPreview);
      observer.observe(frameRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', fitPreview);
    return () => window.removeEventListener('resize', fitPreview);
  }, [fitPreview]);

  return (
    <div
      ref={frameRef}
      className={`book-review-page-preview ${className}`.trim()}
      data-testid={testId}
      style={{ aspectRatio: `${pageSize.width} / ${pageSize.height}` }}
    >
      <div
        className="book-review-canonical-scale"
        style={{
          width: pageSize.width,
          height: pageSize.height,
          transform: `translate(-50%, -50%) scale(${scale})`
        }}
      >
        <CanonicalPageRenderer
          page={page}
          canvasSize={canvasSize}
          ariaLabel={ariaLabel}
          resolveImageUrl={resolveImageUrl}
        />
      </div>
    </div>
  );
};

export default ReviewPagePreview;
